<template>
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewSSHCollector || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        SSH Runner Configurations - {{ filteredConfigs.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="mb-4 text-base flex space-x-3 justify-between">
        <button v-if="canAddSSHCollector || user?.is_staff"
          @click="$router.push({ name: 'AddSSHCollector' })"
          class="bg-blue-700 text-white px-6 py-2 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Add SSH Runner Config
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading SSH Runner Configs..."
      @select="handleOrgSelect"
    />

    <!-- Search and Filter Section -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search configurations by name..."
        />
      </div>

      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="bulkActionOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Bulk Actions"
          class="!w-44"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedConfigs.length === 0"
          :class="[
            'px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
            bulkAction && selectedConfigs.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- SSH Runner Configs Table -->
    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedConfigs"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedConfigs"
      @rowClick="goToConfigDetail"
    >
      <template #name="{ value }">
        {{ value?.replace('SSHCollector-', 'SSH-') }}
      </template>

      <template #linked_entity_name="{ item }">
        <span class="inline-flex items-center">
          <span :class="[
            'px-2 py-1 text-xs rounded-full mr-2',
            item.linked_entity_type === 'probe' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
          ]">
            {{ item.linked_entity_type }}
          </span>
          {{ item.linked_entity_name }}
        </span>
      </template>

      <template #linked_is_wifi_probe="{ value }">
        <span v-if="value === null || value === undefined" class="text-xs text-gray-400">-</span>
        <span v-else :class="[
          'px-2 py-1 text-xs rounded-full',
          value
            ? 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-300'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
        ]">
          {{ value ? "WiFi" : "Server Room" }}
        </span>
      </template>

      <template #pending="{ value }">
        <span :class="[
          'px-2 py-1 text-xs rounded-full',
          value ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
        ]">
          {{ value ? 'Pending' : 'Approved' }}
        </span>
      </template>

      <template #job_counts="{ item }">
        <div class="flex space-x-2 text-xs">
          <span v-if="item.target_count" class="bg-purple-100 text-purple-800 px-2 py-1 rounded">
            Targets: {{ item.target_count }}
          </span>
          <span v-if="item.medicross_count" class="bg-orange-100 text-orange-800 px-2 py-1 rounded">
            Medicross: {{ item.medicross_count }}
          </span>
        </div>
      </template>

      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0">
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="pageSizeOptions"
          valueKey="value"
          labelKey="label"
          placeholder="Page size"
          class="!w-36"
          @change="updatePagination"
        />
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingItem">
      <div
        @click.self="deletingItem=false"
        class="items-center justify-center inset-0 absolute bg-gray-600 bg-opacity-50 flex z-10">
        <div class="bg-white p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 dark:bg-gray-900 w-[65%] h-[35%] m-auto shadow-xl items-center text-center z-50">
          <div class="mb-2 text-base flex space-x-2 justify-between">
            <span class="font-semibold">Are you sure you want to delete the following?</span>
            <button @click="deletingItem=false" class="hover:bg-white dark:hover:bg-gray-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="overflow-auto w-[90%] h-[70%] m-auto items-center text-center mb-4 bg-gray-100 dark:bg-gray-800 rounded">
            <div class="my-6">
              <p class="font-semibold justify-left flex pl-4">SSH Runner Configs: {{ selectedConfigs.length }}</p>
              <p v-for="item in selectedConfigs" :key="item.id" class="font-medium justify-left flex pl-4">{{ item.name }}</p>
            </div>
          </div>
          <div class="text-base flex space-x-2 justify-center mt-6">
            <button
              class="rounded-md text-gray-500 dark:text-white dark:hover:bg-gray-900 hover:text-red-500 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
              @click="confirmDelete">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                <line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>
              </svg>
            </button>
            <button
              class="bg-blue-600 text-white px-6 py-1 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
              @click="deletingItem=false">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPersistedPageSize, setPersistedPageSize } from "@/composables/usePersistedPageSize";
import { getAllSSHCollectorConfigs, deleteSSHCollectorConfig } from "@/services/sshCollectorService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError } from "@/utils/errorMessages";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

import GlassSelect from "@/components/GlassSelect.vue";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
export default {
  name: "SSHCollectorView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    GlassSelect,
    OrganizationTabs,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      configs: [],
      searchQuery: "",
      filteredConfigs: [],
      currentPage: 1,
      itemsPerPage: getPersistedPageSize(),
      selectedConfigs: [],
      deletingItem: false,
      bulkAction: "",
      columns: [
        { key: "name", label: "Name", sortable: true, width: "23%" },
        { key: "linked_entity_name", label: "Linked To", sortable: true, width: "18%" },
        { key: "linked_is_wifi_probe", label: "Type", sortable: true, width: "12%" },
        { key: "job_counts", label: "Jobs", sortable: false, width: "20%" },
        { key: "pending", label: "Status", sortable: true, width: "10%" },
        { key: "created_date", label: "Created", sortable: true, width: "17%" },
      ],
      canAddSSHCollector: false,
      canDeleteSSHCollector: false,
      canViewSSHCollector: false,
      error: "",
      errorCode: "",
    };
  },
  computed: {
    pageSizeOptions() {
      return [
        { value: 10, label: '10 per page' },
        { value: 20, label: '20 per page' },
        { value: 50, label: '50 per page' },
        { value: 100, label: '100 per page' },
        { value: -1, label: 'All' },
      ];
    },
    bulkActionOptions() {
      const opts = [];
      if (this.canDeleteSSHCollector || this.user?.is_staff) {
        opts.push({ value: 'delete', label: 'Delete selected items' });
      }
      return opts;
    },

    user() {
      return useStore.getUser();
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredConfigs.length / this.itemsPerPage);
    },
    paginatedConfigs() {
      if (this.itemsPerPage === -1) {
        return this.filteredConfigs;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredConfigs.slice(start, end);
    },
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.checkPermissions();
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(this.fetchConfigs);
  },
  methods: {
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchConfigs);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddSSHCollector = await useStore.hasPermission("Can add SSH Collector Config");
        this.canDeleteSSHCollector = await useStore.hasPermission("Can delete SSH Collector Config");
        this.canViewSSHCollector = await useStore.hasPermission("Can view SSH Collector Config");
      }
    },
    async fetchConfigs(schemaName = null) {
      try {
        const configs = await getAllSSHCollectorConfigs(schemaName);
        this.configs = [...configs];
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching SSH collector configs:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    goToConfigDetail(config) {
      this.$router.push({
        name: "SSHCollectorDetails",
        params: { id: config.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedConfigs(selectedItems) {
      this.selectedConfigs = selectedItems.filter((item) => !item.marked_for_deletion);
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete") {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      await this.deleteSelectedConfigs();
      this.deletingItem = false;
      this.bulkAction = "";
    },
    async deleteSelectedConfigs() {
      if (this.selectedConfigs.length === 0) {
        alert("No configs selected for deletion");
        return;
      }
      try {
        const deletePromises = this.selectedConfigs.map((config) =>
          deleteSSHCollectorConfig(config.id, this.tenant.currentSchemaName?.value)
        );
        await Promise.all(deletePromises);
        this.fetchConfigs(this.tenant.currentSchemaName?.value);
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedConfigs = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting configs. Please try again.");
      }
    },
    applyFilters() {
      const query = (this.searchQuery || "").toLowerCase();
      this.filteredConfigs = (this.configs || []).filter((config) => {
        const name = (config?.name || "").toLowerCase();
        const linkedName = (config?.linked_entity_name || "").toLowerCase();
        return name.includes(query) || linkedName.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
      setPersistedPageSize(this.itemsPerPage);
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
